/**
 * Dev/staging boot metrics for `DataProvider` fetches.
 *
 * Enable with `localStorage.setItem('kincode:dataContextBootMetrics', '1')` then reload,
 * or when `Configs.devEnvironment` is true. After login settles (~3s), open the console or
 * call `window.__KINCODE_DATA_CONTEXT_BOOT__.summary()`.
 *
 * Used to baseline DataProvider network work before/after migration slices.
 */

type BootFetchRecord = {
  name: string;
  startedAt: number;
  endedAt: number | null;
  durationMs: number | null;
  ok: boolean | null;
  errorMessage?: string;
};

type BootMetricsSummary = {
  enabled: boolean;
  sessionStartedAt: number;
  fetchCount: number;
  inFlight: number;
  totalDurationMs: number;
  byName: Record<
    string,
    {
      count: number;
      totalDurationMs: number;
      failures: number;
    }
  >;
  fetches: BootFetchRecord[];
};

declare global {
  interface Window {
    __KINCODE_DATA_CONTEXT_BOOT__?: {
      summary: () => BootMetricsSummary;
      reset: () => void;
      isEnabled: () => boolean;
    };
  }
}

const STORAGE_KEY = 'kincode:dataContextBootMetrics';

/** @type {BootFetchRecord[]} */
let records: BootFetchRecord[] = [];
let sessionStartedAt = Date.now();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let installedOnWindow = false;
/** Session flag (e.g. Configs.devEnvironment) — does not require localStorage. */
let forceEnabled = false;

/**
 * @returns {boolean}
 */
export function isDataContextBootMetricsEnabled(): boolean {
  if (forceEnabled) return true;
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage?.getItem(STORAGE_KEY) === '1') return true;
  } catch {
    // ignore storage failures
  }
  return false;
}

/**
 * Enable for this JS session (used when `Configs.devEnvironment` is true).
 * @param {boolean} enabled
 */
export function setDataContextBootMetricsForceEnabled(enabled: boolean): void {
  forceEnabled = Boolean(enabled);
  if (forceEnabled) installWindowApi();
}

/**
 * Enable metrics for this browser (persists until cleared).
 */
export function enableDataContextBootMetrics(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // ignore
  }
  installWindowApi();
}

/**
 * @returns {BootMetricsSummary}
 */
export function getDataContextBootMetricsSummary(): BootMetricsSummary {
  const byName: BootMetricsSummary['byName'] = {};
  let totalDurationMs = 0;
  let inFlight = 0;

  for (const record of records) {
    const bucket = byName[record.name] ?? { count: 0, totalDurationMs: 0, failures: 0 };
    bucket.count += 1;
    if (record.durationMs != null) {
      bucket.totalDurationMs += record.durationMs;
      totalDurationMs += record.durationMs;
    } else {
      inFlight += 1;
    }
    if (record.ok === false) bucket.failures += 1;
    byName[record.name] = bucket;
  }

  return {
    enabled: isDataContextBootMetricsEnabled(),
    sessionStartedAt,
    fetchCount: records.length,
    inFlight,
    totalDurationMs,
    byName,
    fetches: [...records],
  };
}

export function resetDataContextBootMetrics(): void {
  records = [];
  sessionStartedAt = Date.now();
}

function installWindowApi(): void {
  if (typeof window === 'undefined' || installedOnWindow) return;
  installedOnWindow = true;
  window.__KINCODE_DATA_CONTEXT_BOOT__ = {
    summary: getDataContextBootMetricsSummary,
    reset: resetDataContextBootMetrics,
    isEnabled: isDataContextBootMetricsEnabled,
  };
}

function scheduleFlushLog(): void {
  if (typeof window === 'undefined') return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    const summary = getDataContextBootMetricsSummary();
    // eslint-disable-next-line no-console
    console.info('[dataContextBootMetrics] summary', summary);
  }, 3000);
}

/**
 * Time a DataProvider fetch. No-op when metrics are disabled.
 *
 * @template T
 * @param {string} name
 * @param {() => Promise<T>} run
 * @returns {Promise<T>}
 */
export async function trackDataContextFetch<T>(name: string, run: () => Promise<T>): Promise<T> {
  if (!isDataContextBootMetricsEnabled()) {
    return run();
  }

  installWindowApi();
  const startedAt = performance.now();
  const record: BootFetchRecord = {
    name,
    startedAt,
    endedAt: null,
    durationMs: null,
    ok: null,
  };
  records.push(record);
  scheduleFlushLog();

  try {
    const result = await run();
    record.endedAt = performance.now();
    record.durationMs = record.endedAt - startedAt;
    record.ok = true;
    scheduleFlushLog();
    return result;
  } catch (error) {
    record.endedAt = performance.now();
    record.durationMs = record.endedAt - startedAt;
    record.ok = false;
    record.errorMessage = error instanceof Error ? error.message : String(error);
    scheduleFlushLog();
    throw error;
  }
}
